'use client'

import { useEffect } from 'react'

import { useAuth } from '@/auth/useAuth'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { useLocation, useNavigate } from '@/routing'

/**
 * Routes that own their own auth/registration UI and must never be
 * redirected away from. `/register` is the wizard itself — excluding it is
 * what prevents this gate from looping.
 */
const EXCLUDED_PREFIXES = [
  '/register',
  '/login',
  '/logout',
  '/auth',
  '/forgot-password',
]

/**
 * Global gate (mounted once in the authenticated app shell): routes a
 * signed-in user who has not completed onboarding (`onboardingCompletedAt`
 * still null — e.g. an SSO JIT-provisioned account) to the register wizard,
 * which starts at `minStep` for an authenticated user (see
 * `getRegisterWizardMinStep`) and never revisits Welcome/Credentials.
 *
 * No-loop guarantees:
 * - `/register` (and the other excluded routes) is skipped, so the redirect
 *   never fires while already on the wizard;
 * - `auth.loading` is gated so a not-yet-resolved session never reads as
 *   "needs onboarding";
 * - once the wizard completes, `onboardingCompletedAt` becomes non-null in
 *   the refreshed auth context (`auth.signin(updatedUser)`), so this effect
 *   simply stops firing on the next render.
 */
export const OnboardingRedirectGate = () => {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const onExcludedRoute = EXCLUDED_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  )

  useEffect(() => {
    if (auth.loading || onExcludedRoute) {
      return
    }

    const user = auth.user
    if (user && !user.fakeUser && user.onboardingCompletedAt == null) {
      navigate(UserRoutes.Register, { replace: true })
    }
  }, [auth.loading, auth.user, onExcludedRoute, navigate])

  return null
}
